/**
 * MandantPrintView — Print-friendly Read-Only-Ansicht aller Mandanten-Daten.
 *
 * Pfad: /mandant/case/:caseId/print
 * Eigenes Layout (kein AppFrame), damit Print-CSS sauber greift und keine
 * Header/Footer-Navigation den Druck verschmutzt.
 *
 * URL-Param `?autoprint=1` triggert `window.print()` 300ms nach Mount —
 * die Mandant-Self-Service-Page nutzt diesen Convenience-Link.
 *
 * Inhalt:
 *   - Petschwork-Logo + Kopf
 *   - Stand der Eingaben — Mandant: [Name], Aktenzeichen: [Kürzel], Datum: [heute]
 *   - Stammdaten-Tabelle
 *   - Pro Wizard-Modul: Eingaben formatiert (Euro, Datum DE, Prozent)
 *   - Pro Dokument: Name, Typ, Upload-Datum (kein Inhalt)
 *   - Footer mit Petschwork-Adresse + Datenschutz-Hinweis
 *
 * Print-CSS: @media print versteckt den „Drucken"-Button und entfernt
 * Browser-typische Hintergrundfarben für sauberen Schwarz-Weiß-Druck.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Printer, ChevronLeft } from 'lucide-react';
import { de } from '../lib/i18n/de';
import { useAuthOrSession } from '../lib/authOrSession';
import { getCase, type CaseRow } from '../lib/supabaseCases';
import { listCaseDocuments, type CaseDocument, DOCUMENT_TYPES } from '../lib/supabaseDocuments';
import { selectStepStatus, useWizardStore } from '../lib/store/wizard';
import type { WizardCaseState, ResourcesDraft } from '../lib/store/wizard';
import { presentDimensionFields } from '../lib/store/dimensionFieldPresenter';
import { STEPS, TOTAL_STEPS, type StepKey } from '../components/hub/dimensions';
import { caseToDraft, type StammdatenDraft } from '../lib/stammdaten/stammdatenValidation';
import { isDemoCaseId, DEMO_CASE_ROW, DEMO_DOCUMENTS } from '../lib/demo/demoFixtures';

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      caseRow: CaseRow;
      stammdaten: StammdatenDraft | null;
      documents: ReadonlyArray<CaseDocument>;
    }
  | { kind: 'error'; msg: string };

export function MandantPrintView() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const autoprint = search.get('autoprint') === '1';
  const { isMock } = useAuthOrSession();
  const isDemo = isMock || isDemoCaseId(caseId);

  const ensureCase = useWizardStore((s) => s.ensureCase);
  const caseState = useWizardStore((s) => s.cases[caseId]);
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    ensureCase(caseId);
  }, [caseId, ensureCase]);

  useEffect(() => {
    let cancelled = false;
    if (isDemo) {
      setLoad({
        kind: 'ready',
        caseRow: DEMO_CASE_ROW,
        stammdaten: caseToDraft(DEMO_CASE_ROW),
        documents: DEMO_DOCUMENTS,
      });
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      try {
        const [c, docs] = await Promise.all([
          getCase(caseId),
          listCaseDocuments(caseId, { onlyActive: true }),
        ]);
        if (cancelled) return;
        if (!c) {
          setLoad({ kind: 'error', msg: 'Mandat nicht gefunden oder kein Zugriff.' });
          return;
        }
        setLoad({ kind: 'ready', caseRow: c, stammdaten: caseToDraft(c), documents: docs });
      } catch (err) {
        if (!cancelled) {
          setLoad({
            kind: 'error',
            msg: err instanceof Error ? err.message : 'Daten konnten nicht geladen werden.',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, isDemo]);

  // Auto-Print 300ms nach Datenladung (gibt Browsern Zeit für Layout)
  useEffect(() => {
    if (!autoprint) return;
    if (load.kind !== 'ready') return;
    if (typeof window === 'undefined') return;
    const t = window.setTimeout(() => {
      window.print();
    }, 300);
    return () => {
      window.clearTimeout(t);
    };
  }, [autoprint, load.kind]);

  return (
    <>
      {/* Print-CSS: in scoped <style> direkt im Tree, kein globales Stylesheet */}
      <style>{`
        @page {
          size: A4;
          margin: 18mm 16mm;
        }
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-page-break { page-break-before: always; }
          .print-keep { page-break-inside: avoid; }
          a { color: inherit !important; text-decoration: none !important; }
          /* Tailwind background colors entfernen für sauberen S/W-Druck */
          .bg-stone-50, .bg-stone-100, .bg-emerald-50, .bg-amber-50 {
            background-color: white !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-white">
        {/* Top-Bar: nur am Bildschirm */}
        <div className="no-print mx-auto max-w-3xl px-5 pt-6 md:px-8 md:pt-8">
          <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between">
            <button
              type="button"
              onClick={() => {
                void navigate(`/mandant/case/${caseId}`);
              }}
              className="inline-flex min-h-[44px] items-center gap-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              <ChevronLeft size={14} aria-hidden="true" />
              {de.print.backLink}
            </button>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== 'undefined') window.print();
              }}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm bg-stone-900 px-5 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              <Printer size={14} aria-hidden="true" />
              {de.print.printButton}
            </button>
          </div>
        </div>

        {/* Druck-Inhalt */}
        <article className="mx-auto max-w-3xl px-5 pb-16 pt-8 md:px-8">
          {load.kind === 'loading' && (
            <p className="font-serif text-base italic text-stone-500">Daten werden geladen …</p>
          )}
          {load.kind === 'error' && (
            <p className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 font-serif text-sm text-red-800">
              {load.msg}
            </p>
          )}
          {load.kind === 'ready' && (
            <PrintBody
              caseRow={load.caseRow}
              stammdaten={load.stammdaten}
              documents={load.documents}
              caseState={caseState ?? null}
            />
          )}
        </article>
      </div>
    </>
  );
}

// ============================================================================
// Print Body
// ============================================================================

function PrintBody({
  caseRow,
  stammdaten,
  documents,
  caseState,
}: {
  caseRow: CaseRow;
  stammdaten: StammdatenDraft | null;
  documents: ReadonlyArray<CaseDocument>;
  caseState: WizardCaseState | null;
}) {
  const today = useMemo(() => new Date().toLocaleDateString('de-DE'), []);
  const ref = caseRow.jc_file_number ?? `Mandat ${caseRow.id.slice(0, 8).toUpperCase()}`;

  return (
    <>
      {/* Logo + Kopf */}
      <header className="border-b border-stone-300 pb-6">
        <div className="flex items-center gap-3">
          <img src="/petschwork-logo.png" alt="Petschwork Consulting" className="h-12 w-auto" />
          <div>
            <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500">
              {de.print.headerLabel}
            </p>
            <p className="font-serif text-xs italic text-stone-500">{de.brand.tagline}</p>
          </div>
        </div>
        <h1 className="mt-6 font-serif text-3xl font-black leading-tight tracking-tight text-stone-900 md:text-4xl">
          {de.print.title}
        </h1>
        <p className="mt-2 font-serif text-sm italic text-stone-600">{de.print.subtitle}</p>

        <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">
          <PrintKv label={de.print.caseLabelMandant} value={caseRow.client_full_name ?? '—'} />
          <PrintKv label={de.print.caseLabelReference} value={ref} />
          <PrintKv label={de.print.caseLabelCaseId} value={caseRow.id.slice(0, 8).toUpperCase()} />
          <PrintKv
            label={de.print.exportedAt.replace('{date}', today).replace(/^Stand: /, '')}
            value={today}
          />
        </dl>
      </header>

      {/* Stammdaten */}
      <section className="mt-8 print-keep">
        <h2 className="font-serif text-xl font-bold tracking-tight text-stone-900">
          {de.print.sectionStammdaten}
        </h2>
        {stammdaten === null ? (
          <p className="mt-2 font-serif text-sm italic text-stone-500">{de.print.fieldEmpty}</p>
        ) : (
          <dl className="mt-3 space-y-1.5">
            <PrintKv label="Name" value={stammdaten.clientFullName || '—'} />
            <PrintKv label="Geburtsdatum" value={formatDateIso(stammdaten.clientBirthdate)} />
            <PrintKv label="Anschrift" value={stammdaten.clientAddress || '—'} multiline />
            <PrintKv label="Branche" value={stammdaten.businessBranch || '—'} />
            <PrintKv label="Rechtsform" value={stammdaten.businessLegalForm || '—'} />
            <PrintKv
              label="Beginn der Selbständigkeit"
              value={formatDateIso(stammdaten.businessStartDate)}
            />
            <PrintKv label="Steuernummer" value={stammdaten.businessTaxId || '—'} />
            <PrintKv
              label="Kurzbeschreibung"
              value={stammdaten.businessDescription || '—'}
              multiline
            />
          </dl>
        )}
      </section>

      {/* Dokumente */}
      <section className="mt-8 print-keep">
        <h2 className="font-serif text-xl font-bold tracking-tight text-stone-900">
          {de.print.sectionDocuments}
        </h2>
        {documents.length === 0 ? (
          <p className="mt-2 font-serif text-sm italic text-stone-500">
            Keine Dokumente hochgeladen.
          </p>
        ) : (
          <ul className="mt-3 list-disc space-y-1 pl-5 font-serif text-sm text-stone-800">
            {documents.map((d) => (
              <li key={d.id}>
                {de.print.docLine
                  .replace('{filename}', d.original_filename)
                  .replace('{type}', DOCUMENT_TYPES[d.doc_type]?.label ?? d.doc_type)
                  .replace('{date}', new Date(d.uploaded_at).toLocaleDateString('de-DE'))}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Wizard-Module */}
      <section className="mt-8">
        <h2 className="font-serif text-xl font-bold tracking-tight text-stone-900">
          {de.print.sectionModules}
        </h2>
        <p className="mt-1 font-serif text-xs italic text-stone-600">
          {caseState
            ? STEPS.filter((s) => selectStepStatus(caseState, s.id) === 'complete').length
            : 0}{' '}
          / {TOTAL_STEPS} Module abgeschlossen
        </p>
        <div className="mt-3 space-y-5">
          {STEPS.map((s) => (
            <PrintModuleCard key={s.id} stepId={s.id} caseState={caseState} />
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-12 border-t border-stone-300 pt-6">
        <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500">
          {de.print.sectionFooter}
        </p>
        <p className="mt-2 font-serif text-xs leading-relaxed text-stone-700">
          {de.dsgvo.selfService.footerControllerAddress}
        </p>
        <p className="mt-3 font-serif text-xs italic leading-relaxed text-stone-600">
          {de.print.footerNote}
        </p>
      </footer>
    </>
  );
}

function PrintModuleCard({
  stepId,
  caseState,
}: {
  stepId: StepKey;
  caseState: WizardCaseState | null;
}) {
  const step = STEPS.find((s) => s.id === stepId);
  if (!step) return null;
  const status = caseState ? selectStepStatus(caseState, stepId) : 'untouched';
  const statusLabel =
    status === 'complete'
      ? 'Abgeschlossen'
      : status === 'in-progress'
        ? 'In Bearbeitung'
        : 'Noch offen';

  return (
    <div className="border-l-2 border-stone-300 pl-4 print-keep">
      <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500">
        Modul {step.nummer}
      </p>
      <p className="font-serif text-base font-bold tracking-tight text-stone-900">{step.name}</p>
      <p className="font-serif text-xs italic text-stone-600">
        {de.print.moduleStatus}: {statusLabel}
      </p>
      <div className="mt-2">
        {caseState ? (
          <PrintModuleBody stepId={stepId} caseState={caseState} />
        ) : (
          <p className="font-serif text-sm italic text-stone-500">{de.print.fieldEmpty}</p>
        )}
      </div>
    </div>
  );
}

function PrintModuleBody({ stepId, caseState }: { stepId: StepKey; caseState: WizardCaseState }) {
  if (stepId === 'triage') {
    const value = caseState.triage.legalBasis;
    if (!value) return <Empty />;
    return (
      <dl className="space-y-1">
        <PrintKv label="Rechtsgrundlage" value={mapLegalBasis(value)} />
      </dl>
    );
  }

  if (stepId === 'resources') {
    return <PrintResourcesBody resources={caseState.resources} />;
  }

  const dim = caseState.dimensionData[stepId];
  if (!dim) return <Empty />;
  const presented = presentDimensionFields(stepId, dim.fields);
  if (presented.length === 0) return <Empty />;

  return (
    <dl className="space-y-1">
      {presented.map((f, i) => (
        <PrintKv key={i} label={f.label} value={f.display} multiline />
      ))}
    </dl>
  );
}

function PrintResourcesBody({ resources }: { resources: ResourcesDraft }) {
  const r = resources;
  const profit =
    r.revenueCents !== null && r.costsCents !== null ? r.revenueCents - r.costsCents : null;
  const kduTotal = r.kdu.kaltCents + r.kdu.nebenCents + r.kdu.heizCents;
  return (
    <dl className="space-y-1">
      <PrintKv label="Umsatz / Monat" value={formatEuroOrEmpty(r.revenueCents)} />
      <PrintKv label="Betriebsausgaben / Monat" value={formatEuroOrEmpty(r.costsCents)} />
      <PrintKv label="Daraus errechneter Gewinn" value={formatEuroOrEmpty(profit)} />
      <PrintKv label="Abrechenbare Stunden / Monat" value={formatHoursOrEmpty(r.hoursPerMonth)} />
      <PrintKv label="Liquide Mittel" value={formatEuroOrEmpty(r.liquidityCents)} />
      <PrintKv
        label="Bedarfsgemeinschaft"
        value={
          r.bgComposition.length > 0
            ? `${String(r.bgComposition.length)} Personen (${r.bgComposition.map((m) => mapBgRole(m.role)).join(', ')})`
            : '—'
        }
      />
      <PrintKv
        label="Kosten der Unterkunft (gesamt)"
        value={kduTotal > 0 ? formatEuroOrEmpty(kduTotal) : '—'}
      />
    </dl>
  );
}

function PrintKv({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-4">
      <dt className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500 md:w-56 md:flex-shrink-0">
        {label}
      </dt>
      <dd
        className={`font-serif text-sm text-stone-900 ${multiline ? 'whitespace-pre-line' : 'tabular-nums'}`}
      >
        {value}
      </dd>
    </div>
  );
}

function Empty() {
  return <p className="font-serif text-sm italic text-stone-500">{de.print.fieldEmpty}</p>;
}

// ============================================================================
// Formatters (pure)
// ============================================================================

function formatDateIso(iso: string | null | undefined): string {
  if (!iso || iso.length === 0) return '—';
  try {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString('de-DE');
  } catch {
    return iso;
  }
}

function formatEuroOrEmpty(cents: number | null): string {
  if (cents === null) return '—';
  const euros = cents / 100;
  return `${euros.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function formatHoursOrEmpty(h: number | null): string {
  if (h === null) return '—';
  if (Number.isInteger(h)) return `${String(h)} h / Monat`;
  return `${h.toLocaleString('de-DE', { maximumFractionDigits: 1 })} h / Monat`;
}

function mapLegalBasis(value: string): string {
  const opts = de.steps.triage.legalBasisOptions as Readonly<Record<string, string>>;
  return opts[value] ?? value;
}

function mapBgRole(role: string): string {
  switch (role) {
    case 'antragsteller':
      return 'Antragsteller:in';
    case 'partner':
      return 'Partner:in';
    case 'kind':
      return 'Kind';
    default:
      return role;
  }
}
